// Het tabblad Beroepen van Configuratie (bouwrun baan C2, ownerantwoord 13).
//
// Per sector welke UWV-beroepen erbij horen. De standaard is een best guess van
// 08-10 (config/beroepen-per-sector.yaml: de UWV-indeling gelegd tegen de
// sector als geheel); een propositie krijgt de beroepen van al haar sectoren.
// Een beheerder haalt hier een beroep weg (×) of voegt er een toe (keuzelijst,
// gegroepeerd per beroepsklasse), en zet een sector met één klik terug.
//
// Opslaan SCHRIJFT in de gedeelde database (tabel instellingen, migratie 0033;
// sleutel per sector, de volledige lijst als waarde; alleen toevoegen, de
// laatste rij wint). Daarom dezelfde afspraken als op het tabblad Instellingen:
//  · één opslag tegelijk: tijdens het opslaan staan alle kaarten dicht
//    (aria-disabled, niet `disabled`, zodat de focus niet van de pagina valt);
//  · de server controleert (sof/beroepen_sector.py#valideer_lijst) en het
//    scherm bouwt die controle niet na; een 400 draagt een zin die er
//    letterlijk staat;
//  · geen sleutels op het scherm: de beheerder leest de namen van UWV.
//
// Dit tabblad haalt pas op als het voor het eerst opent: de rest van
// Configuratie komt uit één antwoord, en dat blijft zo.

import { useMemo, useState } from "react";

import { Card } from "../../components/ui";
import { EmptyReason, LoadError } from "../../components/Empty";
import { api } from "../../lib/api";
import { useResource } from "../../lib/useResource";
import type { BeroepenResponse, SectorBeroepen, UwvBeroep } from "../../contracts/api/beheer";
import { color, radius, size, weight } from "../../tokens";
import { datum, noot } from "./BronnenStukken";
import { KruisIcoon, TerugIcoon, VinkIcoon } from "./Iconen";
import { invoer, knopDonker, knopLicht } from "./stijl";
import { leesbaar } from "./woorden";

type Opslaan = (sector: string, body: { beroepen?: string[]; terugNaarStandaard?: boolean;
  motivering?: string | null }) => Promise<string | null>;

/** De keuzelijst: per beroepsklasse de groepen die er nog niet in staan, en
 *  daarna de klassen zelf. Volgorde en namen van de server. */
export function keuzeGroepen(
  indeling: readonly UwvBeroep[],
  gekozen: readonly string[],
): { label: string; opties: UwvBeroep[] }[] {
  const al = new Set(gekozen);
  const vrij = indeling.filter((b) => b.koppelbaar && !al.has(b.code));
  const perKlasse = new Map<string, UwvBeroep[]>();
  for (const b of vrij) {
    if (b.indeling !== "Beroepsgroep") continue;
    const k = b.klasseNaam ?? "Overig";
    perKlasse.set(k, [...(perKlasse.get(k) ?? []), b]);
  }
  const groepen = [...perKlasse.entries()]
    .sort(([a], [b]) => a.localeCompare(b, "nl"))
    .map(([label, opties]) => ({
      label,
      opties: [...opties].sort((x, y) => x.naam.localeCompare(y.naam, "nl")),
    }));
  const klassen = vrij.filter((b) => b.indeling === "Beroepsklasse");
  if (klassen.length) groepen.push({ label: "Brede beroepsklassen", opties: klassen });
  return groepen;
}

function zelfde(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((x, i) => x === b[i]);
}

export function Beroepen() {
  const bron = useResource<BeroepenResponse>(() => api.beroepen(), []);
  // Wat de server na een opslag terugstuurde, per sector, over het eerste antwoord heen.
  const [eigen, setEigen] = useState<Record<string, SectorBeroepen>>({});
  const [bezig, setBezig] = useState<string | null>(null);

  const d = bron.data;
  const sectoren = useMemo(
    () => (d ? d.sectoren.map((s) => eigen[s.sector] ?? s) : []),
    [d, eigen],
  );

  if (!d) {
    return (
      <Card style={{ padding: 18 }}>
        {bron.error ? (
          <LoadError reason={bron.error} onRetry={bron.reload} />
        ) : (
          <EmptyReason reason="de beroepen per sector worden opgehaald" />
        )}
      </Card>
    );
  }

  const opslaan: Opslaan = async (sector, body) => {
    setBezig(sector);
    try {
      const nieuw = await api.beroepenOpslaan({ sector, ...body });
      setEigen((e) => ({ ...e, [sector]: nieuw }));
      return null;
    } catch (err) {
      return leesbaar(err, "De beroepen konden niet worden opgeslagen — probeer het zo nog eens.");
    } finally {
      setBezig(null);
    }
  };

  return (
    <div data-testid="configuratie-beroepen" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <p style={{ ...noot, marginTop: 0, fontSize: 13 }}>{d.uitleg}</p>
      {sectoren.map((s) => (
        <SectorKaart
          key={s.sector}
          s={s}
          indeling={d.indeling}
          bezig={bezig === s.sector}
          dicht={bezig !== null}
          opslaan={opslaan}
        />
      ))}
      {d.bewustNiet.length > 0 && (
        <Card style={{ padding: 16 }}>
          <div style={{ fontSize: size.meta, fontWeight: weight.medium, color: color.ink }}>
            Bewust niet gekoppeld
          </div>
          <p style={{ ...noot, marginTop: 4 }}>
            Beroepen die vrijwel geheel hbo of wo zijn. Je kunt ze bij een sector zelf toevoegen.
          </p>
          <ul style={{ margin: "6px 0 0", paddingLeft: 18, fontSize: 12.5, color: color.soft, lineHeight: 1.6 }}>
            {d.bewustNiet.map((b) => (
              <li key={b.code}>
                {b.naam} <span style={{ color: color.faint }}>— {b.reden}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

function SectorKaart({
  s,
  indeling,
  bezig,
  dicht,
  opslaan,
}: {
  s: SectorBeroepen;
  indeling: readonly UwvBeroep[];
  bezig: boolean;
  dicht: boolean;
  opslaan: Opslaan;
}) {
  const huidig = s.beroepen.map((b) => b.code);
  const [lijst, setLijst] = useState<string[]>(huidig);
  const [basis, setBasis] = useState<string[]>(huidig);
  const [motivering, setMotivering] = useState("");
  const [fout, setFout] = useState<string | null>(null);
  // Kwam er een nieuw antwoord van de server (na een opslag), dan begint de
  // kaart opnieuw vanaf wat de server vastlegde.
  if (!zelfde(basis, huidig)) {
    setBasis(huidig);
    setLijst(huidig);
  }
  const naam = useMemo(() => new Map(indeling.map((b) => [b.code, b])), [indeling]);
  const perCode = new Map(s.beroepen.map((b) => [b.code, b]));
  const veranderd = !zelfde(lijst, huidig);
  const kan = veranderd && !dicht;

  const verstuur = async (body: Parameters<Opslaan>[1]) => {
    if (dicht) return;
    setFout(null);
    const zin = await opslaan(s.sector, { ...body, motivering: motivering.trim() || null });
    if (zin) setFout(zin);
    else setMotivering("");
  };

  return (
    <Card style={{ padding: 16 }}>
      <section data-sector={s.sector} aria-busy={bezig || undefined}>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: "4px 10px" }}>
          <div style={{ fontSize: 15, fontWeight: weight.medium, color: color.ink }}>{s.naam}</div>
          {s.proposities.length > 0 && (
            <div style={{ fontSize: 12, color: color.faint }}>
              gebruikt door {s.proposities.map((p) => p.naam).join(", ")}
            </div>
          )}
        </div>
        <div data-testid="stand" style={{ fontSize: 12, color: color.faint, marginTop: 3 }}>
          {s.aangepast
            ? `Aangepast${s.door ? ` door ${s.door}` : ""}${s.op ? ` op ${datum(s.op)}` : ""}` +
              (s.motivering ? ` — ${s.motivering}` : "")
            : "Standaard (best guess 08-10)"}
        </div>

        <ul style={{ listStyle: "none", padding: 0, margin: "10px 0 0", display: "flex", flexWrap: "wrap", gap: 6 }}>
          {lijst.map((code) => {
            const b = naam.get(code);
            const regel = perCode.get(code);
            const klasse = b?.indeling === "Beroepsklasse";
            return (
              <li
                key={code}
                data-chip={code}
                title={regel?.reden ?? undefined}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  maxWidth: "100%",
                  border: `1px solid ${color.line}`,
                  borderRadius: radius.input,
                  padding: "4px 6px 4px 9px",
                  fontSize: 12.5,
                  color: color.body,
                  background: color.surface,
                }}
              >
                <span style={{ overflowWrap: "anywhere" }}>
                  {b?.naam ?? "Onbekend beroep"}
                  {klasse && <span style={{ color: color.faint }}> · klasse</span>}
                  {b && !b.metCijfers && <span style={{ color: color.faint }}> · nog geen cijfers</span>}
                </span>
                <button
                  type="button"
                  aria-label={`Haal ${b?.naam ?? "dit beroep"} weg`}
                  aria-disabled={dicht || undefined}
                  onClick={() => {
                    if (dicht) return;
                    setLijst((l) => l.filter((c) => c !== code));
                  }}
                  style={{ background: "none", border: "none", padding: 2, cursor: dicht ? "default" : "pointer", color: color.faint, display: "inline-flex" }}
                >
                  <KruisIcoon />
                </button>
              </li>
            );
          })}
          {lijst.length === 0 && (
            <li style={{ fontSize: 12.5, color: color.faint }}>Geen beroepen bij deze sector.</li>
          )}
        </ul>

        {/* Voorbehoud en reden bij wat nu vastligt (de server stuurt ze). */}
        {s.beroepen.some((b) => b.voorbehoud) && (
          <ul style={{ ...noot, paddingLeft: 18, marginBottom: 0 }}>
            {s.beroepen
              .filter((b) => b.voorbehoud)
              .map((b) => (
                <li key={b.code}>
                  {b.naam}: {b.voorbehoud}
                </li>
              ))}
          </ul>
        )}

        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12, alignItems: "center" }}>
          <label style={{ flex: "1 1 220px", minWidth: 0 }}>
            <span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>
              Beroep toevoegen aan {s.naam}
            </span>
            <select
              value=""
              aria-disabled={dicht || undefined}
              onChange={(e) => {
                const code = e.target.value;
                if (!code || dicht) return;
                setLijst((l) => (l.includes(code) ? l : [...l, code]));
              }}
              style={{ ...invoer, padding: "7px 9px" }}
            >
              <option value="">+ Beroep toevoegen…</option>
              {keuzeGroepen(indeling, lijst).map((g) => (
                <optgroup key={g.label} label={g.label}>
                  {g.opties.map((o) => (
                    <option key={o.code} value={o.code}>
                      {o.naam}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </label>
          <input
            type="text"
            aria-label={`Waarom (optioneel) — ${s.naam}`}
            placeholder="Waarom (optioneel)"
            value={motivering}
            readOnly={dicht}
            onChange={(e) => setMotivering(e.target.value)}
            style={{ ...invoer, flex: "1 1 200px", minWidth: 0, padding: "7px 9px" }}
          />
          <button
            type="button"
            aria-disabled={!kan}
            onClick={() => {
              if (!kan) return;
              void verstuur({ beroepen: lijst });
            }}
            style={{ ...knopDonker(!kan), display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <VinkIcoon /> {bezig ? "bezig…" : "Opslaan"}
          </button>
          {veranderd && (
            <button
              type="button"
              aria-disabled={dicht || undefined}
              onClick={() => {
                if (dicht) return;
                setLijst(huidig);
                setFout(null);
              }}
              style={knopLicht}
            >
              Ongedaan maken
            </button>
          )}
          {s.aangepast && (
            <button
              type="button"
              aria-disabled={dicht || undefined}
              onClick={() => {
                if (dicht) return;
                void verstuur({ terugNaarStandaard: true });
              }}
              style={{ ...knopLicht, display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <TerugIcoon /> Terug naar de best guess
            </button>
          )}
        </div>
        {fout && (
          <div role="alert" style={{ fontSize: 12.5, color: color.ink, marginTop: 8, fontWeight: weight.medium }}>
            {fout}
          </div>
        )}
      </section>
    </Card>
  );
}
